import React, { useState, useEffect, useMemo } from 'react';
import { findDuplicateLeads, emailDomain } from '../utils/leadDuplicates';

function Field({ label, value, onChange, type = 'text', options, placeholder }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#22302a', marginBottom: 6 }}>{label}</label>
      {options ? (
        <div style={{ position: 'relative' }}>
          <select value={value} onChange={e => onChange(e.target.value)}
            style={{ width: '100%', height: 42, borderRadius: 8, border: '1.5px solid #dee4de', padding: '0 36px 0 12px', fontSize: 13, color: value ? '#22302a' : '#93a89a', background: '#f7faf7', outline: 'none', fontFamily: 'inherit', cursor: 'pointer', appearance: 'none' }}>
            <option value=""></option>
            {options.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#66796c" strokeWidth="2.5" style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </div>
      ) : (
        <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
          style={{ width: '100%', height: 42, borderRadius: 8, border: '1.5px solid #dee4de', padding: '0 12px', fontSize: 13, color: '#22302a', background: '#f7faf7', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', transition: 'border-color .15s' }}
          onFocus={e => { e.target.style.borderColor = '#159c5f'; }}
          onBlur={e => { e.target.style.borderColor = '#dee4de'; }} />
      )}
    </div>
  );
}

const EMPTY = { email: '', vorname: '', nachname: '', anrede: '', form: '', job: '', tel: '', handy: '', quelle: '' };

export default function NewContactModal({ onClose, onCreate, onMergeIntoLead, onOpenLead, existingLeads = [], isMobileFullscreen = false }) {
  const [form, setForm] = useState({ ...EMPTY });
  const [err, setErr] = useState('');
  const [dupWarning, setDupWarning] = useState(null);
  const [dupOverride, setDupOverride] = useState(false);

  function setField(field) { return v => setForm(f => ({ ...f, [field]: v })); }

  function candidateForDup() {
    const name = [form.vorname, form.nachname].filter(Boolean).join(' ') || (form.email || '').trim() || '';
    const em = (form.email || '').trim();
    return {
      name,
      email: em,
      tel: form.tel || form.handy,
      domain: emailDomain(em),
    };
  }

  const liveDuplicates = useMemo(() => {
    const c = candidateForDup();
    if (!c.name && !c.email && !c.tel) return [];
    return findDuplicateLeads(existingLeads, c);
  }, [form, existingLeads]);

  useEffect(() => {
    if (dupOverride) return;
    if (liveDuplicates.length > 0) setDupWarning(liveDuplicates);
    else if (!dupWarning) setDupWarning(null);
  }, [liveDuplicates, dupOverride]); // eslint-disable-line

  function validate() {
    if (!form.vorname.trim() && !form.nachname.trim() && !form.email.trim()) {
      setErr('Bitte mindestens Vorname, Nachname oder E-Mail angeben.');
      return false;
    }
    setErr('');
    return true;
  }

  function buildLead() {
    const name = [form.vorname, form.nachname].filter(Boolean).join(' ') || form.email || 'Unbekannt';
    return {
      name,
      firstName: form.vorname || '',
      lastName: form.nachname || '',
      company: form.job || '–',
      email: form.email || '–',
      tel: form.tel || form.handy || '–',
      status: 'new',
      list: 'nk',
      score: 0,
      jobs: 0,
      activity: new Date().toLocaleDateString('de-DE'),
      city: '',
    };
  }

  function buildMergePatch() {
    const patch = {};
    const name = [form.vorname, form.nachname].filter(Boolean).join(' ');
    if (name) {
      patch.name = name;
      patch.firstName = form.vorname || '';
      patch.lastName = form.nachname || '';
    }
    const em = form.email.trim().toLowerCase();
    if (em) patch.email = em;
    const tel = (form.tel || form.handy).trim();
    if (tel) patch.tel = tel;
    if (form.job.trim()) patch.company = form.job.trim();
    return patch;
  }

  function runDupCheck() {
    return findDuplicateLeads(existingLeads, candidateForDup());
  }

  function handleCreate() {
    if (!validate()) return;
    const dups = runDupCheck();
    if (dups.length > 0 && !dupOverride) {
      setDupWarning(dups);
      return;
    }
    onCreate(buildLead());
    onClose();
  }

  function handleCreateAndNext() {
    if (!validate()) return;
    const dups = runDupCheck();
    if (dups.length > 0 && !dupOverride) {
      setDupWarning(dups);
      return;
    }
    onCreate(buildLead());
    setForm({ ...EMPTY });
    setErr('');
    setDupWarning(null);
    setDupOverride(false);
  }

  function handleMerge(leadId) {
    if (!validate()) return;
    const patch = buildMergePatch();
    if (Object.keys(patch).length === 0) {
      setErr('Keine Daten zum Zusammenführen – bitte Felder ausfüllen.');
      return;
    }
    onMergeIntoLead?.(leadId, patch);
    onClose();
  }

  const showDup = (dupWarning?.length > 0 || liveDuplicates.length > 0) && !dupOverride;
  const dupList = dupWarning?.length ? dupWarning : liveDuplicates;

  return (
    <div onClick={e => { if (!isMobileFullscreen && e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: isMobileFullscreen ? 'transparent' : 'rgba(15,23,42,0.42)', zIndex: 700, display: 'flex', alignItems: isMobileFullscreen ? 'flex-start' : 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: isMobileFullscreen ? 0 : 14, width: isMobileFullscreen ? '100%' : 520, height: isMobileFullscreen ? '100%' : 'auto', maxHeight: isMobileFullscreen ? '100%' : '92vh', display: 'flex', flexDirection: 'column', boxShadow: isMobileFullscreen ? 'none' : '0 20px 60px rgba(15,23,42,0.22)', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ padding: isMobileFullscreen ? '16px 16px' : '20px 24px', background: 'linear-gradient(135deg, #0d9488 0%, #159c5f 100%)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          {isMobileFullscreen ? (
            <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.18)', border: 'none', cursor: 'pointer', color: '#fff', width: 36, height: 36, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
            </button>
          ) : null}
          <div style={{ fontSize: isMobileFullscreen ? 17 : 18, fontWeight: 700, color: '#fff', flex: 1, textAlign: isMobileFullscreen ? 'center' : 'left', marginLeft: isMobileFullscreen ? 0 : 0 }}>Kontakt erstellen</div>
          {!isMobileFullscreen ? (
            <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.18)', border: 'none', cursor: 'pointer', color: '#fff', width: 30, height: 30, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          ) : <div style={{ width: 36 }}/>}
        </div>

        {/* Form */}
        <div style={{ flex: 1, overflowY: 'auto', padding: isMobileFullscreen ? '16px 16px 4px' : '20px 24px 4px', WebkitOverflowScrolling: 'touch' }}>
          {err && <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#dc2626', marginBottom: 16 }}>{err}</div>}
          {showDup && dupList.length > 0 && (
            <div style={{ background: 'rgba(245,158,11,0.08)', border: '1.5px solid rgba(245,158,11,0.4)', borderRadius: 10, padding: '12px 14px', marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#d97706', marginBottom: 6 }}>Ähnlicher Lead existiert</div>
              <div style={{ fontSize: 12, color: '#92400e', marginBottom: 10 }}>
                Gleiche E-Mail/Telefon oder gleicher Name mit gleicher Domain – vor dem Anlegen prüfen oder zusammenführen.
              </div>
              {dupList.slice(0, 5).map(d => (
                <div key={d.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, color: '#78350f' }}>· {d.name} <span style={{ color: '#a16207' }}>({d.company || '–'})</span></span>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {onOpenLead && (
                      <button type="button" onClick={() => { onOpenLead(d); onClose(); }}
                        style={{ height: 30, padding: '0 12px', borderRadius: 7, background: '#fff', border: '1.5px solid #cbd8cd', color: '#4a5a4f', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        Lead öffnen
                      </button>
                    )}
                    {onMergeIntoLead && (
                      <button type="button" onClick={() => handleMerge(d.id)}
                        style={{ height: 30, padding: '0 12px', borderRadius: 7, background: '#fff', border: '1.5px solid #d97706', color: '#b45309', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        Zusammenführen
                      </button>
                    )}
                  </div>
                </div>
              ))}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
                <button type="button" onClick={() => setDupOverride(true)}
                  style={{ height: 32, padding: '0 14px', borderRadius: 7, background: 'transparent', border: '1.5px solid #cbd8cd', color: '#66796c', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  Trotzdem als neuen Lead anlegen
                </button>
                <button type="button" onClick={() => { setDupWarning(null); setDupOverride(false); }}
                  style={{ height: 32, padding: '0 14px', borderRadius: 7, background: 'var(--bg4, #f0f5f0)', border: '1px solid #e2ebe3', color: '#66796c', fontSize: 12, cursor: 'pointer' }}>
                  Daten anpassen
                </button>
              </div>
            </div>
          )}
          <Field label="E-Mail" value={form.email} onChange={setField('email')} type="email" placeholder="name@firma.de" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Vorname" value={form.vorname} onChange={setField('vorname')} />
            <Field label="Nachname" value={form.nachname} onChange={setField('nachname')} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Herr/Frau" value={form.anrede} onChange={setField('anrede')} options={['Herr', 'Frau', 'Divers']} />
            <Field label="Per Sie/Du" value={form.form} onChange={setField('form')} options={['Sie', 'Du']} />
          </div>
          <Field label="Jobbezeichnung" value={form.job} onChange={setField('job')} placeholder="z.B. Geschäftsführer" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Telefonnummer" value={form.tel} onChange={setField('tel')} type="tel" placeholder="+49 …" />
            <Field label="Handynummer" value={form.handy} onChange={setField('handy')} type="tel" placeholder="+49 …" />
          </div>
          <Field label="Kontaktquelle" value={form.quelle} onChange={setField('quelle')} options={['Webseite', 'Empfehlung', 'LinkedIn', 'Kaltakquise', 'Messe', 'Sonstige']} />
        </div>

        {/* Buttons */}
        <div style={{ padding: isMobileFullscreen ? '14px 16px' : '16px 24px', borderTop: '1px solid #e8efe9', display: 'flex', gap: 10, flexWrap: 'wrap', flexShrink: 0, paddingBottom: isMobileFullscreen ? 'calc(14px + env(safe-area-inset-bottom, 0px))' : '16px' }}>
          <button type="button" onClick={handleCreate}
            style={{ height: 40, padding: '0 22px', borderRadius: 8, background: '#e96c4a', border: 'none', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            Erstellen
          </button>
          <button type="button" onClick={handleCreateAndNext}
            style={{ height: 40, padding: '0 16px', borderRadius: 8, background: 'transparent', border: '1.5px solid #e96c4a', color: '#e96c4a', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            Erstellen und weiteren Eintrag erstellen
          </button>
          <button type="button" onClick={onClose}
            style={{ height: 40, padding: '0 16px', borderRadius: 8, background: 'transparent', border: '1.5px solid #cbd8cd', color: '#66796c', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
            Abbrechen
          </button>
        </div>
      </div>
    </div>
  );
}
